/**
 * Whether the thread has stopped with work left hanging.
 *
 * A tool call is shown as running from the status assistant-ui gives its part,
 * and that status never changes when the run ends without a result for it (the
 * server errored, the stream dropped, the tool was interrupted and nothing
 * resumed it). The row would spin forever and say nothing. This hook says when
 * the thread is idle for real: not running, not waiting on an interrupt the
 * client is answering (a Word tool at work in the document), and so for a
 * moment, since the run's end and the interrupt's arrival are separate events.
 */
import { useAuiState } from "@assistant-ui/react";
import { useLangGraphInterruptState } from "@assistant-ui/react-langgraph";
import { useEffect, useState } from "react";

/** How long the thread must be idle before hanging tool calls count as dead. */
const SETTLE_MS = 1500;

export const useThreadStalled = (): boolean => {
  const running = useAuiState((state) => state.thread.isRunning);
  const interrupted = useLangGraphInterruptState() != null;
  const idle = !running && !interrupted;
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!idle) {
      setSettled(false);
      return undefined;
    }
    const timer = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [idle]);
  return settled;
};
